import { useEffect, useId, useState } from "react";
import { CAMPO, Recado } from "./ui";
import { mensagemDoCatalogo } from "../lib/adminCatalogo";
import { mascararMoeda, moedaDeNumero } from "../lib/mascaras";
import {
  levarProducaoAgora,
  lerProducaoDoProduto,
  mudarEstado,
  salvarAula,
  tirarAula,
  type CamposDaAula,
  type CentralDaAula,
  type ItemDeProducao,
  type ProducaoDoProduto,
  type StatusDaProducao,
  type TipoDaProducao,
} from "../lib/producao";

/**
 * Admin › Catálogo › Produtos › o produto › **Produção de conteúdo** (000313-
 * 000315 — o G2 do Plano de Melhorias da Equipe de Produtos, e a decisão 3 de
 * Victor: "produção de conteúdo mora no hub").
 *
 * Uma linha por aula (ou material) a produzir, com UM estado que inclui a
 * revisão técnica: planejada → em produção → em revisão → (ajustes pedidos →)
 * aprovada → publicada. O painel de cima é DERIVADO no banco — produzidas,
 * faltam, atrasadas, previsão de término e o valor investido —, e a tela não
 * soma nem compara data nenhuma.
 *
 * Desde a 000322, a aula GRAVADA, com professor, ENTREGUE vai para a Central de
 * Professores, para ser paga (a ligação do G2 com o C6, decidida por Victor em
 * 28/09). O valor de lá — duração × valor-hora do contrato — é o que conta como
 * investido; o digitado aqui é a estimativa. Cada linha diz o que a Central sabe
 * dela, e o bloco da Central tem o «Levar agora», a mesma leva que o cron de lá
 * dispara de 15 em 15 minutos.
 */

/** AAAA-MM-DD → DD/MM/AAAA, sem `new Date` (a coluna é `date`; o fuso a poria um dia antes). */
const dia = (iso: string) => iso.slice(0, 10).split("-").reverse().join("/");
const reais = (n: number) => n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

/**
 * Os rótulos do que a CENTRAL guarda. Eles não moram no banco daqui — o status
 * do pagamento é vocabulário de lá —, e o valor cru aparece quando um rótulo
 * faltar, em vez de sumir.
 */
const PAGAMENTO: Record<string, string> = {
  nao_liberado: "pagamento não liberado",
  aguardando_liberacao: "conferida, aguardando liberação",
  liberado_pagamento: "liberada para pagamento",
  pago: "paga",
};
const ORIGEM_DO_VALOR_HORA: Record<string, string> = {
  producao: "o valor-hora da própria aula, digitado lá",
  contrato: "o valor-hora do contrato",
  cadastro: "o valor-hora do cadastro",
};
const CAMPO_PENDENTE: Record<string, string> = { professor: "o professor", duracao: "a duração", entrega: "a data da entrega" };

/** Hoje em São Paulo, AAAA-MM-DD — o teto do campo da entrega (quem recusa é o banco). */
const hojeEmSaoPaulo = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "America/Sao_Paulo", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());

export default function AdminCatalogoProdutoProducao({ curso, aoMudar }: { curso: string; aoMudar?: () => void }) {
  const [dados, setDados] = useState<ProducaoDoProduto | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [versao, setVersao] = useState(0);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);
  // O formulário: fechado, incluindo (null) ou editando uma aula.
  const [formulario, setFormulario] = useState<{ item: ItemDeProducao | null } | null>(null);

  useEffect(() => {
    let vivo = true;
    void (async () => {
      const r = await lerProducaoDoProduto(curso);
      if (!vivo) return;
      if (r.ok) {
        setDados(r as ProducaoDoProduto);
        setErro(null);
      } else setErro(mensagemDoCatalogo(r));
    })();
    return () => {
      vivo = false;
    };
  }, [curso, versao]);

  function mudou(texto: string, ok: boolean) {
    setRecado({ tipo: ok ? "ok" : "erro", texto });
    if (ok) {
      setVersao((v) => v + 1);
      aoMudar?.();
    }
  }

  const r = dados?.resumo;

  return (
    <section className="surface rise p-6 sm:p-7" role="group" aria-labelledby="titulo-producao" data-producao-do-produto>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-2xl">
          <h3 id="titulo-producao" className="font-display text-lg font-bold text-titulo">
            Produção de conteúdo
          </h3>
          <p className="mt-1 text-sm text-cinza">
            Uma linha por aula ou material a produzir, com o estado — que inclui a revisão técnica. Produzida é
            aprovada ou publicada; o valor conta como investido quando a aula é entregue (em revisão em diante).
            A aula gravada, com professor, vai para a Central de Professores quando é entregue: lá o valor é
            calculado pela duração e pelo valor-hora do professor, e é esse valor que conta aqui.
          </p>
        </div>
        {!formulario && dados && (
          <button type="button" className="btn-contorno min-h-10 text-sm" onClick={() => setFormulario({ item: null })} data-incluir-aula>
            + Incluir aula
          </button>
        )}
      </div>

      {formulario && dados && (
        <FormularioDaAula
          curso={curso}
          dados={dados}
          item={formulario.item}
          aoFechar={() => setFormulario(null)}
          aoSalvar={(texto) => {
            setFormulario(null);
            mudou(texto, true);
          }}
        />
      )}

      {recado && <div data-recado-producao><Recado tipo={recado.tipo} texto={recado.texto} /></div>}
      {erro && <p className="mt-3 text-sm text-erro-tinta" role="alert">{erro}</p>}
      {!dados && !erro && <p className="mt-3 text-sm text-cinza">Carregando…</p>}

      {dados && r && (
        <>
          <dl className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-resumo-producao>
            <Numero
              chave="produzidas"
              rotulo="Produzidas"
              valor={`${r.produzidas} de ${r.total}`}
              apoio={r.total === 0 ? "nenhuma aula no controle ainda" : "aprovadas ou publicadas"}
            />
            <Numero
              chave="faltam"
              rotulo="Faltam"
              valor={String(r.faltam)}
              apoio={`${r.em_revisao} em revisão · ${r.ajustes} com ajustes pedidos${r.atrasadas > 0 ? ` · ${r.atrasadas} atrasada(s)` : ""}`}
            />
            <Numero
              chave="previsao"
              rotulo="Previsão de término"
              valor={r.previsao_termino ? dia(r.previsao_termino) : "—"}
              apoio={
                r.faltam === 0
                  ? "nada falta"
                  : r.faltam_sem_prazo > 0
                    ? `${r.faltam_sem_prazo} sem prazo — a previsão não as conta`
                    : "o maior prazo entre as que faltam"
              }
            />
            <Numero
              chave="investido"
              rotulo="Valor investido"
              valor={reais(r.valor_investido)}
              apoio={
                `de ${reais(r.valor_previsto)} previsto` +
                (r.investido_da_central > 0 ? ` · ${reais(r.investido_da_central)} calculados na Central` : "") +
                (r.sem_valor > 0 ? ` · ${r.sem_valor} sem valor` : "")
              }
            />
          </dl>

          {(r.central.vao > 0 || dados.fora_do_controle.length > 0) && (
            <BlocoDaCentral
              resumo={r.central}
              fora={dados.fora_do_controle}
              // Só relê: o recado da leva é do bloco, e repeti-lo no recado geral
              // punha a mesma frase duas vezes na tela (visto na foto de 28/09).
              aoLevar={() => {
                setVersao((v) => v + 1);
                aoMudar?.();
              }}
            />
          )}

          {dados.itens.length === 0 ? (
            <p className="mt-4 text-sm text-cinza" data-sem-aulas>
              Nenhuma aula no controle ainda. Inclua a primeira — o painel acima passa a contar sozinho.
            </p>
          ) : (
            <ul className="mt-5 divide-y divide-borda">
              {dados.itens.map((i) => (
                <LinhaDaAula
                  key={i.id}
                  item={i}
                  estados={dados.estados}
                  tipos={dados.tipos}
                  aoEditar={() => setFormulario({ item: i })}
                  aoMudar={mudou}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

function Numero({ chave, rotulo, valor, apoio }: { chave: string; rotulo: string; valor: string; apoio: string }) {
  return (
    <div className="rounded-xl border border-borda p-4" data-indicador-producao={chave}>
      <dt className="text-xs font-bold tracking-widest text-cinza uppercase">{rotulo}</dt>
      <dd className="mt-1 font-display text-2xl font-bold text-titulo" data-valor>
        {valor}
      </dd>
      <dd className="mt-0.5 text-xs text-cinza">{apoio}</dd>
    </div>
  );
}

/**
 * O que está na Central de Professores, em números, e o «Levar agora» (000322).
 * Os números vêm do banco (`resumo.central`); a leva é a porta, com a mesma
 * função que o cron da Central chama. O recado da leva fica AQUI, e não no
 * recado geral: é a resposta a este botão.
 */
function BlocoDaCentral({
  resumo,
  fora,
  aoLevar,
}: {
  resumo: ProducaoDoProduto["resumo"]["central"];
  fora: ProducaoDoProduto["fora_do_controle"];
  aoLevar: () => void;
}) {
  const [levando, setLevando] = useState(false);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);

  async function levar() {
    setLevando(true);
    setRecado(null);
    const r = await levarProducaoAgora();
    setLevando(false);
    setRecado({ tipo: r.ok ? "ok" : "erro", texto: mensagemDoCatalogo(r) });
    // Relê a produção: os valores de lá chegam junto com a leva.
    if (r.ok) aoLevar();
  }

  const partes = [
    resumo.vao === 1 ? "1 aula gravada entregue vai para a Central" : `${resumo.vao} aulas gravadas entregues vão para a Central`,
    `${resumo.levadas} lá, na versão de agora`,
    resumo.faltam > 0 ? `${resumo.faltam} faltando levar` : null,
    resumo.com_erro > 0 ? `${resumo.com_erro} com erro` : null,
    resumo.com_pendencia > 0 ? `${resumo.com_pendencia} com mudança que a Central não aplicou` : null,
  ].filter(Boolean);

  return (
    <div className="mt-4 rounded-xl border border-borda p-4" data-central-da-producao>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-2xl">
          <p className="font-semibold text-tinta">Na Central de Professores</p>
          <p className="mt-1 text-sm text-cinza" data-central-resumo>
            {partes.join(" · ")}. A leva roda sozinha de 15 em 15 minutos, e traz de volta o valor que a Central
            calculou — ou que a equipe de lá digitou.
          </p>
        </div>
        <button type="button" className="btn-contorno min-h-10 text-sm" disabled={levando} onClick={() => void levar()} data-levar-producao>
          {levando ? "Levando…" : "Levar agora"}
        </button>
      </div>
      {recado && <div data-recado-central><Recado tipo={recado.tipo} texto={recado.texto} /></div>}
      {fora.length > 0 && (
        <div className="mt-3" data-fora-do-controle>
          <p className="text-xs font-bold tracking-widest text-cinza uppercase">Tiradas daqui que a Central ainda guarda</p>
          <ul className="mt-1 space-y-1">
            {fora.map((f) => (
              <li key={f.item_id} className="text-sm text-tinta" data-fora={f.titulo ?? f.item_id}>
                {f.titulo ?? "(aula sem título)"}
                {" — "}
                {f.erro
                  ? `a última leva falhou: ${f.erro}`
                  : f.falta_retirar
                    ? "a Central ainda não sabe que ela saiu; vai na próxima leva"
                    : f.status_financeiro && f.status_financeiro !== "nao_liberado"
                      ? `saiu do pagamento, mas lá ela já estava ${PAGAMENTO[f.status_financeiro] ?? f.status_financeiro}: fica marcada, para a equipe de lá resolver`
                      : "saiu do pagamento na Central"}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * O que a Central sabe desta aula, numa frase — e, à parte, o que precisa de
 * alguém: o erro da última leva, ou a mudança que a Central não aplicou porque
 * o pagamento já tinha sido conferido lá.
 */
function LinhaDaCentral({ item, central }: { item: ItemDeProducao; central: CentralDaAula }) {
  const valor = central.valor ?? 0;
  let frase: string;
  if (central.estado === "retirada") {
    frase =
      central.status_financeiro && central.status_financeiro !== "nao_liberado"
        ? `Saiu do pagamento na Central, mas lá ela já estava ${PAGAMENTO[central.status_financeiro] ?? central.status_financeiro}: fica marcada, para a equipe de lá resolver.`
        : "Saiu do pagamento na Central.";
  } else if (central.estado === null) {
    // Com erro, "vai na próxima leva" prometeria uma solução que não vem
    // sozinha (o professor que não existe lá continua não existindo): o porquê
    // fica na linha de baixo.
    frase = central.erro
      ? "Ainda não está na Central."
      : "Ainda não está na Central: vai na próxima leva, ou em «Levar agora».";
  } else {
    const comoFoiCalculado =
      central.valor_origem === "calculado" && !central.falta_levar && !central.pendencia && item.duracao_min && central.valor_hora !== null
        ? ` — ${item.duracao_min} min × ${reais(central.valor_hora)} por hora, ${ORIGEM_DO_VALOR_HORA[central.valor_hora_origem ?? ""] ?? "o valor-hora do professor"}`
        : central.valor_origem === "manual"
          ? ", digitado lá"
          : "";
    const valorLa =
      central.valor_origem === "padrao"
        ? "Na Central, sem valor-hora do professor: vale a estimativa daqui, até alguém pôr o valor-hora na ficha dele lá"
        : `Na Central: ${reais(valor)}${comoFoiCalculado}`;
    frase =
      `${valorLa} · ${PAGAMENTO[central.status_financeiro ?? ""] ?? central.status_financeiro ?? "sem status"}.` +
      (central.falta_levar ? " Mudou aqui depois da última leva: a versão nova vai na próxima." : "");
  }
  const pendentes = (central.pendencia?.campos ?? []).map((c) => CAMPO_PENDENTE[c] ?? c);

  return (
    <div className="mt-1" data-central-da-aula data-estado-central={central.estado ?? "fora"}>
      <p className="text-sm text-tinta" data-central-frase>
        {frase}
      </p>
      {central.erro && (
        <p className="mt-1 text-sm text-alerta-tinta" role="alert" data-central-erro>
          A última leva não chegou à Central: {central.erro}. Cada leva tenta de novo.
        </p>
      )}
      {central.pendencia && (
        <p className="mt-1 text-sm text-alerta-tinta" data-central-pendencia>
          A Central já tinha conferido o pagamento: {pendentes.length ? pendentes.join(" e ") : "o que mudou aqui"} mudou
          depois, e não foi aplicado lá. Quem ajusta é a equipe da Central — o valor à mão, ou reabrindo o mês.
        </p>
      )}
    </div>
  );
}

function LinhaDaAula({
  item,
  estados,
  tipos,
  aoEditar,
  aoMudar,
}: {
  item: ItemDeProducao;
  estados: ProducaoDoProduto["estados"];
  tipos: ProducaoDoProduto["tipos"];
  aoEditar: () => void;
  aoMudar: (texto: string, ok: boolean) => void;
}) {
  const id = useId();
  const [pedindoAjustes, setPedindoAjustes] = useState(false);
  const [nota, setNota] = useState("");
  const [tirando, setTirando] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);

  async function trocarEstado(status: StatusDaProducao) {
    // "Ajustes pedidos" exige a nota: o seletor abre o campo em vez de mandar.
    if (status === "ajustes") {
      setPedindoAjustes(true);
      return;
    }
    setEnviando(true);
    const r = await mudarEstado(item.id, status, null);
    setEnviando(false);
    aoMudar(mensagemDoCatalogo(r), r.ok);
  }

  async function pedirAjustes() {
    setEnviando(true);
    const r = await mudarEstado(item.id, "ajustes", nota);
    setEnviando(false);
    if (r.ok) {
      setPedindoAjustes(false);
      setNota("");
    }
    aoMudar(mensagemDoCatalogo(r), r.ok);
  }

  async function tirar() {
    setEnviando(true);
    const r = await tirarAula(item.id, motivo);
    setEnviando(false);
    if (r.ok) setTirando(false);
    aoMudar(mensagemDoCatalogo(r), r.ok);
  }

  const detalhes = [
    item.disciplina,
    item.professor,
    item.duracao_min ? `${item.duracao_min} min` : null,
    item.prazo ? `prazo ${dia(item.prazo)}` : "sem prazo",
    item.entregue_em ? `entregue em ${dia(item.entregue_em)}` : null,
    // Com o valor da Central, o digitado aqui é só a estimativa — e a tela diz.
    item.valor !== null ? (item.valor_da_central ? `estimado aqui em ${reais(item.valor)}` : reais(item.valor)) : null,
  ].filter(Boolean);

  return (
    <li className="py-4" data-aula={item.titulo} data-status={item.status}>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <p className="min-w-0 font-semibold text-tinta">
          {item.titulo}{" "}
          <span className="ml-1 rounded-full bg-chip px-2 py-0.5 text-xs font-semibold text-tinta">
            {tipos.find((t) => t.valor === item.tipo)?.rotulo ?? item.tipo}
          </span>{" "}
          <span className="ml-1 rounded-full bg-chip px-2 py-0.5 text-xs font-semibold text-tinta" data-rotulo-status>
            {item.status_rotulo}
          </span>
          {item.atrasada && (
            <span className="ml-2 rounded-full bg-alerta-fundo px-2 py-0.5 text-xs font-semibold text-alerta-tinta" data-atrasada>
              atrasada
            </span>
          )}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor={`${id}-estado`} className="sr-only">
            Estado de {item.titulo}
          </label>
          <select
            id={`${id}-estado`}
            className={`${CAMPO} min-h-10 w-auto text-sm`}
            value={pedindoAjustes ? "ajustes" : item.status}
            disabled={enviando}
            onChange={(e) => void trocarEstado(e.target.value as StatusDaProducao)}
            data-estado
          >
            {estados.map((e) => (
              <option key={e.valor} value={e.valor}>
                {e.rotulo}
              </option>
            ))}
          </select>
          <button type="button" className="btn-contorno min-h-10 text-sm" onClick={aoEditar} data-editar-aula>
            Editar
          </button>
          <button type="button" className="btn-contorno min-h-10 text-sm" onClick={() => setTirando(true)} data-tirar-aula>
            Tirar
          </button>
        </div>
      </div>
      <p className="mt-1 text-xs text-cinza">{detalhes.join(" · ")}</p>
      {item.central && <LinhaDaCentral item={item} central={item.central} />}
      {item.status === "ajustes" && item.revisao_nota && (
        <p className="mt-1 text-sm text-alerta-tinta" data-nota-da-revisao>
          Ajustes pedidos: {item.revisao_nota}
        </p>
      )}
      {item.observacao && <p className="mt-1 whitespace-pre-line text-sm text-tinta">{item.observacao}</p>}

      {pedindoAjustes && (
        <div className="mt-3 rounded-xl border border-borda p-3" data-pedir-ajustes-de={item.titulo}>
          <label htmlFor={`${id}-nota`} className="block text-xs font-bold tracking-widest text-cinza uppercase">
            Quais ajustes a revisão pediu?
          </label>
          <textarea
            id={`${id}-nota`}
            className={`${CAMPO} mt-1`}
            rows={2}
            maxLength={1000}
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            data-nota-ajustes
          />
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" className="pill-cta" disabled={enviando} onClick={() => void pedirAjustes()} data-pedir-ajustes>
              {enviando ? "Enviando…" : "Pedir ajustes"}
            </button>
            <button type="button" className="btn-contorno min-h-10 text-sm" onClick={() => setPedindoAjustes(false)}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {tirando && (
        <div className="mt-3 rounded-xl border border-borda p-3" data-tirar-de={item.titulo}>
          <label htmlFor={`${id}-motivo`} className="block text-xs font-bold tracking-widest text-cinza uppercase">
            Por que esta aula sai do controle?
          </label>
          <input
            id={`${id}-motivo`}
            className={`${CAMPO} mt-1`}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            data-motivo-tirar
          />
          <p className="mt-1 text-xs text-cinza">A linha inteira fica no histórico do produto, com o motivo.</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" className="pill-cta" disabled={enviando} onClick={() => void tirar()} data-confirmar-tirar>
              {enviando ? "Tirando…" : "Tirar do controle"}
            </button>
            <button type="button" className="btn-contorno min-h-10 text-sm" onClick={() => setTirando(false)}>
              Cancelar
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

/**
 * Incluir (item nulo) ou editar uma aula. Na edição, só vai o que MUDOU, e o
 * campo esvaziado vai como `null` — o contrato do salvar: chave ausente não
 * mexe, `null` limpa.
 */
function FormularioDaAula({
  curso,
  dados,
  item,
  aoFechar,
  aoSalvar,
}: {
  curso: string;
  dados: ProducaoDoProduto;
  item: ItemDeProducao | null;
  aoFechar: () => void;
  aoSalvar: (texto: string) => void;
}) {
  const id = useId();
  const [titulo, setTitulo] = useState(item?.titulo ?? "");
  const [tipo, setTipo] = useState<TipoDaProducao>(item?.tipo ?? "gravada");
  const [disciplina, setDisciplina] = useState(item?.disciplina_id ?? "");
  const [professor, setProfessor] = useState(item?.docente_id ?? "");
  const [duracao, setDuracao] = useState(item?.duracao_min ? String(item.duracao_min) : "");
  const [prazo, setPrazo] = useState(item?.prazo ?? "");
  const [valor, setValor] = useState(moedaDeNumero(item?.valor ?? null));
  const [observacao, setObservacao] = useState(item?.observacao ?? "");
  // A data da ENTREGA (000322) só existe na aula entregue, e só lá se corrige:
  // a equipe marca a entrega depois de o professor entregar, e o mês do
  // pagamento na Central depende do dia certo.
  const [entregueEm, setEntregueEm] = useState(item?.entregue_em ?? "");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  function campos(): CamposDaAula {
    const novo: CamposDaAula = {
      titulo,
      tipo,
      disciplina_id: disciplina || null,
      docente_id: professor || null,
      duracao_min: duracao.trim() === "" ? null : Number(duracao),
      prazo: prazo || null,
      valor: mascararMoeda(valor).numero,
      observacao: observacao.trim() || null,
    };
    if (!item) {
      // Na inclusão, o vazio não vai: nulo e ausente dão no mesmo aqui.
      return Object.fromEntries(Object.entries(novo).filter(([, v]) => v !== null && v !== "")) as CamposDaAula;
    }
    // A entrega só vai quando MUDOU; esvaziada, ela vai como está, e quem recusa
    // — dizendo que o caminho é o estado — é o banco.
    if (item.entregue && entregueEm !== (item.entregue_em ?? "")) novo.entregue_em = entregueEm;
    const antes: CamposDaAula = {
      titulo: item.titulo,
      tipo: item.tipo,
      disciplina_id: item.disciplina_id,
      docente_id: item.docente_id,
      duracao_min: item.duracao_min,
      prazo: item.prazo,
      valor: item.valor,
      observacao: item.observacao,
      entregue_em: item.entregue_em ?? undefined,
    };
    return Object.fromEntries(
      Object.entries(novo).filter(([k, v]) => v !== antes[k as keyof CamposDaAula]),
    ) as CamposDaAula;
  }

  async function salvar() {
    setEnviando(true);
    setErro(null);
    const r = await salvarAula(item?.id ?? null, curso, campos());
    setEnviando(false);
    if (r.ok) aoSalvar(mensagemDoCatalogo(r));
    else setErro(mensagemDoCatalogo(r));
  }

  return (
    <div className="mt-4 rounded-xl border border-borda p-4" data-formulario-aula={item ? "editar" : "incluir"}>
      <p className="font-semibold text-tinta">{item ? `Editar «${item.titulo}»` : "Incluir aula"}</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Campo id={`${id}-titulo`} rotulo="Título">
          <input id={`${id}-titulo`} className={CAMPO} value={titulo} maxLength={200} onChange={(e) => setTitulo(e.target.value)} data-campo-aula="titulo" />
        </Campo>
        <Campo id={`${id}-tipo`} rotulo="Tipo">
          <select id={`${id}-tipo`} className={CAMPO} value={tipo} onChange={(e) => setTipo(e.target.value as TipoDaProducao)} data-campo-aula="tipo">
            {dados.tipos.map((t) => (
              <option key={t.valor} value={t.valor}>
                {t.rotulo}
              </option>
            ))}
          </select>
        </Campo>
        <Campo id={`${id}-disciplina`} rotulo="Disciplina">
          <select id={`${id}-disciplina`} className={CAMPO} value={disciplina} onChange={(e) => setDisciplina(e.target.value)} data-campo-aula="disciplina">
            <option value="">— sem disciplina —</option>
            {dados.disciplinas.map((d) => (
              <option key={d.id} value={d.id}>
                {d.nome}
              </option>
            ))}
          </select>
        </Campo>
        <Campo id={`${id}-professor`} rotulo="Professor">
          <select id={`${id}-professor`} className={CAMPO} value={professor} onChange={(e) => setProfessor(e.target.value)} data-campo-aula="professor">
            <option value="">— sem professor —</option>
            {dados.professores.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nome}
              </option>
            ))}
          </select>
        </Campo>
        <Campo id={`${id}-duracao`} rotulo="Duração (minutos)">
          <input id={`${id}-duracao`} className={CAMPO} inputMode="numeric" value={duracao} onChange={(e) => setDuracao(e.target.value.replace(/\D/g, ""))} data-campo-aula="duracao" />
        </Campo>
        <Campo id={`${id}-prazo`} rotulo="Prazo">
          <input id={`${id}-prazo`} type="date" className={CAMPO} value={prazo} onChange={(e) => setPrazo(e.target.value)} data-campo-aula="prazo" />
        </Campo>
        <Campo id={`${id}-valor`} rotulo="Valor">
          <input id={`${id}-valor`} className={CAMPO} inputMode="numeric" value={valor} onChange={(e) => setValor(mascararMoeda(e.target.value).texto)} data-campo-aula="valor" />
        </Campo>
        <Campo id={`${id}-observacao`} rotulo="Observação">
          <input id={`${id}-observacao`} className={CAMPO} value={observacao} maxLength={2000} onChange={(e) => setObservacao(e.target.value)} data-campo-aula="observacao" />
        </Campo>
        {item?.entregue && (
          <Campo id={`${id}-entregue`} rotulo="Entregue em">
            <input
              id={`${id}-entregue`}
              type="date"
              className={CAMPO}
              value={entregueEm}
              max={hojeEmSaoPaulo()}
              onChange={(e) => setEntregueEm(e.target.value)}
              data-campo-aula="entregue_em"
            />
          </Campo>
        )}
      </div>
      {item?.entregue && item.central?.vai && (
        <p className="mt-2 text-xs text-cinza">
          A data da entrega decide o mês do pagamento na Central. Se ela já conferiu o pagamento desta aula, a data
          nova fica lá como pendência, para a equipe de lá resolver.
        </p>
      )}
      <div className="mt-4 flex flex-wrap gap-3">
        <button type="button" className="pill-cta" disabled={enviando} onClick={() => void salvar()} data-salvar-aula>
          {enviando ? "Salvando…" : item ? "Salvar" : "Incluir"}
        </button>
        <button type="button" className="btn-contorno min-h-10 text-sm" onClick={aoFechar}>
          Cancelar
        </button>
      </div>
      {erro && <Recado tipo="erro" texto={erro} />}
    </div>
  );
}

function Campo({ id, rotulo, children }: { id: string; rotulo: string; children: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-bold tracking-widest text-cinza uppercase">
        {rotulo}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
